import { v4 as uuidv4 } from "uuid";
import {
  TierList,
  TierListTemplate,
  TierListTemplateItem,
  Image,
  TierItem,
} from "@/types/tier-list";
import {
  getImage,
  getTierItemsByTierListId,
  saveImage,
  saveTierItem,
  saveTierList,
} from "@/lib/indexdb";
import { CURRENT_TEMPLATE_VERSION } from "@/lib/template-versioning";

/**
 * Convert a Blob to base64 string
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert a base64 string to Blob
 */
export function base64ToBlob(base64: string): Blob {
  const parts = base64.split(';base64,');
  const contentType = parts[0].split(':')[1];
  const raw = window.atob(parts[1]);
  const rawLength = raw.length;
  const uInt8Array = new Uint8Array(rawLength);

  for (let i = 0; i < rawLength; ++i) {
    uInt8Array[i] = raw.charCodeAt(i);
  }

  return new Blob([uInt8Array], { type: contentType });
}

/**
 * Export a tier list to a template
 */
export async function exportTierListTemplate(
  tierList: TierList
): Promise<TierListTemplate> {
  // Get logo if exists
  let logoBase64: string | undefined;
  if (tierList.logoId) {
    const logoImage = await getImage(tierList.logoId);
    if (logoImage) {
      logoBase64 = await blobToBase64(logoImage.blob);
    }
  }

  // Get all items for this tier list
  const tierItems = await getTierItemsByTierListId(tierList.id);
  const templateItems: TierListTemplateItem[] = [];

  for (const item of tierItems) {
    const image = await getImage(item.imageId);
    if (image) {
      const imageBase64 = await blobToBase64(image.blob);
      templateItems.push({
        id: item.id,
        name: item.name,
        image: imageBase64,
        tierId: item.tierId,
        order: item.order,
      });
    }
  }

  return {
    version: tierList.version || CURRENT_TEMPLATE_VERSION,
    name: tierList.name,
    logo: logoBase64,
    tiers: tierList.tiers,
    items: templateItems,
  };
}

/**
 * Import a template and create a new tier list
 * @param template The template to import
 * @param importedItems Whether to import items with their tier placement
 * @returns The ID of the newly created tier list
 */
export async function importTierListTemplate(
  template: TierListTemplate,
  importedItems: boolean = true
): Promise<string> {
  const tierListId = uuidv4();

  // Import logo
  let logoId: string | undefined;
  if (template.logo) {
    logoId = uuidv4();
    const logoBlob = base64ToBlob(template.logo);
    const logoImage: Image = {
      id: logoId,
      tierListId,
      blob: logoBlob,
      mimeType: logoBlob.type,
    };
    await saveImage(logoImage);
  }

  // Create tier list
  const tierList: TierList = {
    id: tierListId,
    name: template.name,
    tiers: template.tiers,
    logoId,
    version: template.version,
  };
  await saveTierList(tierList);

  // Import items
  if (importedItems && template.items && template.items.length > 0) {
    for (const templateItem of template.items) {
      const imageId = uuidv4();
      const imageBlob = base64ToBlob(templateItem.image);
      const image: Image = {
        id: imageId,
        tierListId,
        blob: imageBlob,
        mimeType: imageBlob.type,
      };
      await saveImage(image);

      const tierItem: TierItem = {
        id: templateItem.id,
        name: templateItem.name,
        imageId,
        tierListId,
        tierId: templateItem.tierId,
        order: templateItem.order,
      };
      await saveTierItem(tierItem);
    }
  }

  return tierListId;
}

/**
 * Download a template as a JSON file
 */
export function downloadTemplate(
  template: TierListTemplate,
  filename?: string
): void {
  const json = JSON.stringify(template, null, 2);
  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename || `${template.name}-template.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Parse a template from a JSON file
 */
export async function parseTemplateFile(file: File): Promise<TierListTemplate> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const template = JSON.parse(e.target?.result as string);
        resolve(template);
      } catch {
        reject(new Error("Invalid template file"));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsText(file);
  });
}
